import { XIcon } from 'lucide-preact';
import { useCallback, useEffect, useRef } from 'preact/hooks';

import AdvertisementOverlay from '@/components/advertisements/AdvertisementOverlay';
import { useIsMobileLayout } from '@/components/LayoutDetector/hooks';
// import Video from '@/components/Video';
import VideoPlayer from '@/components/VideoPlayer';
import { Advertisement } from '@/queries/advertisements/types';
import { Video, VideoV2 } from '@/queries/media/types';
import { useSelector } from '@/redux/helper';
import { selectHasFullHighlights, selectIsFreeUser } from '@/redux/selectors/users';
import { trackMP } from '@/shared/mp';

import HomeVideoPlayer from '../HomeVideoPlayer';

interface HomeHighlightVideoPlayerProps {
  highlight: Video | VideoV2;
  onNextHighlight?: () => void;
  onSwitchContentMode?: () => void;
  miniMode?: boolean;
  onClose?: () => void;
  muted?: boolean;
  onMuteChange?: (muted: boolean) => void;

  hideOnMiniMode?: boolean;
  canPlayVideo: boolean;
  setAdsPlayer: (el: HTMLVideoElement | null) => void;
  advertisement: Advertisement | undefined;
}

const HomeHighlightVideoPlayer = ({
  highlight,
  onNextHighlight,
  miniMode,
  onClose,
  onSwitchContentMode,
  muted,
  onMuteChange,

  hideOnMiniMode,
  canPlayVideo,
  setAdsPlayer,
  advertisement,
}: HomeHighlightVideoPlayerProps) => {
  const isMobile = useIsMobileLayout();
  const isFreeUser = useSelector(selectIsFreeUser);
  const hasFullHighlights = useSelector(selectHasFullHighlights);
  const mainPlayerRef = useRef<HTMLVideoElement>(null);
  const habitat = 'habitat' in highlight ? highlight.habitat : (highlight as Video).habitatId;

  // for users who do not have the explorer feature, show visit-habitat end screen and disable auto-advance
  const showVisitHabitatOverlay = !hasFullHighlights;

  const handleEnd = useCallback(() => {
    if (!showVisitHabitatOverlay) {
      onNextHighlight?.();
    }
  }, [showVisitHabitatOverlay, onNextHighlight]);

  useEffect(() => {
    if (!canPlayVideo) {
      mainPlayerRef.current?.pause();
    } else {
      mainPlayerRef.current?.play();
    }
  }, [canPlayVideo]);

  return (
    <HomeVideoPlayer.Container miniMode={miniMode} hideOnMiniMode={hideOnMiniMode}>
      <HomeVideoPlayer.Player>
        <VideoPlayer
          key={highlight.rawVideoURL}
          videoRef={mainPlayerRef}
          className="mx-auto"
          width="100%"
          height="100%"
          videoURL={highlight.rawVideoURL}
          videoId={highlight._id}
          alt={highlight.title}
          autoPlay={!isFreeUser || canPlayVideo}
          muted={muted}
          controls={!miniMode}
          noOverlay={miniMode}
          showVisitHabitatOverlay={showVisitHabitatOverlay}
          onEnd={handleEnd}
          onLoad={() =>
            trackMP('watch-video', {
              type: 'homeHighlightVideo',
              videoURL: highlight.rawVideoURL,
              title: highlight.title,
              mediaId: highlight._id,
            })
          }
          onMuteChange={onMuteChange}
          analyticsData={{
            habitatName: habitat?.title,
            habitatId: habitat?._id,
            slug: habitat ? `${habitat.zoo.slug}/${habitat.slug}` : undefined,
            title: highlight.title,
            player: 'home-highlight-video',
            videoType: 'videoType' in highlight ? highlight.videoType : highlight.type,
          }}
        >
          {!miniMode && isFreeUser && (
            <AdvertisementOverlay
              muted={muted}
              isVisible={!canPlayVideo}
              adsPlayerRef={(el) => setAdsPlayer(el)}
              advertisement={advertisement}
            />
          )}
        </VideoPlayer>
        {!isMobile && miniMode && (
          <>
            <HomeVideoPlayer.ExpandButton onClick={onSwitchContentMode} />
            {/* Next button on desktop mini player */}
            {onNextHighlight && (
              <div className="absolute bottom-2 right-2 transition-opacity desktop:opacity-0 desktop:group-hover/home-player:opacity-100">
                <button
                  type="button"
                  className="preflight preflight-button z-10 rounded-full bg-white px-3 py-1 text-xs leading-none hover:bg-grey-5"
                  onClick={() => {
                    onNextHighlight();
                    trackMP('home-next-highlight-clicked', {
                      videoURL: highlight.rawVideoURL,
                      title: highlight.title,
                      mediaId: highlight._id,
                    });
                  }}
                >
                  <span>Next</span>
                </button>
              </div>
            )}
            {/* Close button on desktop mini player */}
            <button
              type="button"
              className="preflight preflight-button absolute right-0 top-0 flex items-center justify-center p-2 text-base text-white opacity-0 drop-shadow-lg transition-opacity hover:text-grey-5 group-hover/home-player:opacity-100"
              onClick={onClose}
            >
              <XIcon className="size-[1em]" />
              <span className="sr-only">Close mini player</span>
            </button>
          </>
        )}
      </HomeVideoPlayer.Player>
      {miniMode && isMobile && (
        <div className="flex min-w-0 grow items-center p-2 pr-4">
          <div className="flex min-w-0 grow items-center px-2">
            <div className="mr-4 flex flex-col justify-center text-white">
              <h4 className="preflight preflight-h4 text-sm font-bold">{habitat?.title ?? highlight.title}</h4>
              <span className="text-xs">{habitat?.zoo.name}</span>
            </div>
            <HomeVideoPlayer.ExpandButton onClick={onSwitchContentMode} />
          </div>
          <button type="button" className="preflight preflight-button" onClick={onClose}>
            <span className="flex size-6 items-center justify-center rounded-full bg-white text-primary-green group-hover:bg-grey-5">
              <XIcon className="h-4 w-4" />
            </span>
            <span className="sr-only">Close mini player</span>
          </button>
        </div>
      )}
    </HomeVideoPlayer.Container>
  );
};

export default HomeHighlightVideoPlayer;
